import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DuctRun, type DuctStage, STAGE_COLOR, toDuctStages, type StageRow } from '@monrdv/ui';
import { formatMoney, localized, toCents, type Locale, type StageKind } from '@monrdv/shared';
import { ApiError, request } from '../lib/api.js';
import { ListToolbar, ListWindowNotice, type FilterChip } from '../components/ListToolbar.js';
import { SortHeader } from '../components/SortHeader.js';
import { Pager } from '../components/Pager.js';
import { downloadExport, useListView, type ListView } from '../lib/listView.js';

/**
 * Work, two ways.
 *
 * The system this replaces gave all 4454 jobs the identical status `"active"` and hid the
 * real state one level down, so this screen exists to make state the first thing you see:
 * the board's columns ARE the stages, and every list row carries a duct run.
 *
 * **Board** is for dispatching — one column per stage, cards you can compare at a glance.
 * **List** is for scanning — dense rows, sorted, one screen holds forty jobs. Neither is a
 * fallback for the other, so the choice lives in the URL and survives a reload.
 *
 * No filter is component state. Everything a coordinator would be annoyed to lose on F5 is
 * a search parameter, per the contract in `router.tsx`.
 */

// --- assumed API shapes -----------------------------------------------------------------
// These endpoints do not exist yet; the types below are the responses the screen needs,
// named the way the rest of the codebase names things (camelCase, `id`, `<verb>At`, money as
// `numeric(14,2)` strings rather than floats).

/** `GET /api/pipeline-stages` — the tenant's own pipeline, ordered by position. */
/** One job as both views need it — the board card and the table row want the same facts. */
type JobRow = {
  id: string;
  number: string;
  title: string | null;
  /**
   * The stage, as the API sends it. This was declared as a flat `stageId` that the API has
   * never sent — so every job rendered its pipeline from `undefined`, and all of them looked
   * identical. TypeScript could not catch it: the response is parsed as `JobRow` on trust.
   */
  stage: { id: string; name: Record<string, string>; kind: StageKind; color: string };
  customer: { id: string; name: string };
  /** Null for the 16% of customers with no address on file. */
  address: { line1: string; city: string; postalCode: string | null; note: string | null } | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  assignees: Array<{ id: string; name: string; initials: string; isLead: boolean }>;
  services: Array<{ id: string; name: Record<string, string> }>;
  /** From `job_rollups`, so a list view never sums invoices client-side. */
  rollup: { balance: string; invoicedTotal: string; laborMinutes: number; messageCount: number };
};

/** `GET /api/jobs` — paged, with an accurate total (never the echoed page size). */
type JobListResponse = {
  items: JobRow[];
  nextCursor: string | null;
  total: number;
  page?: number;
  pageSize?: number;
  pageCount?: number;
  sort?: string;
  dir?: 'asc' | 'desc';
  /** Rows the period is holding back, counted by the API rather than guessed at here. */
  outsideWindow: number;
};

/** `GET /api/jobs/board` — grouped server-side: the board is one round trip, not one per column. */
type JobBoardResponse = { columns: Array<{ stageId: string; total: number; items: JobRow[] }> };

/** `GET /api/jobs/filter-options` — everything the dropdowns can offer, in one call. */
type FilterOptions = {
  assignees: Array<{ id: string; name: string; initials: string }>;
  services: Array<{ id: string; name: Record<string, string> }>;
  /** The unassigned filter advertises its own size — 3149 of Hydrokleen's jobs. */
  unassignedCount: number;
};

/**
 * Stage colour comes from the stage's `kind`, not from its imported hex.
 *
 * Three tenants arrived with three unrelated palettes; colouring by meaning means
 * "awaiting payment" is the same amber everywhere, and every colour on screen traces back
 * to a token. DESIGN.md: a status colour is never chosen by hand.
 */
type T = (key: string, vars?: Record<string, string | number>) => string;

/**
 * The jobs route's search contract, mirrored from `router.tsx` so this screen can type the
 * writes it makes. The zod schema there stays the authority — an old bookmark carrying a
 * since-removed filter degrades to the default instead of crashing.
 */
type JobsSearch = {
  search?: string;
  view: 'board' | 'list';
  stage?: string;
  assignee?: string;
  service?: string;
  q?: string;
  unassignedOnly: boolean;
  sort: 'scheduled' | 'created' | 'balance' | 'number' | 'customer' | 'activity';
  dir?: 'asc' | 'desc';
  cursor?: string;
  page?: number;
};

export function JobsScreen() {
  const { t, i18n } = useTranslation();
  const tr = t as T;
  const locale: Locale = i18n.language === 'en' ? 'en' : 'fr';
  const navigate = useNavigate();
  const search: JobsSearch = useSearch({ from: '/jobs' });
  // `view0` rather than `view`, which now names the shared list state below. Board or list is a
  // choice of rendering; everything else about the screen is the same query either way.
  const { view: view0, stage, assignee, service, unassignedOnly } = search;

  /**
   * The single writer for this screen's own filters.
   *
   * The shared ones — search text, period, sort, page — are written by `useListView`, which
   * applies the same rule: a filter change drops the cursor and the page, because page two of
   * the previous filter describes rows that are no longer in the result.
   */
  const setSearch = useCallback(
    (patch: Partial<JobsSearch>) => {
      void navigate({
        to: '/jobs',
        search: { ...search, ...patch, cursor: undefined, page: undefined },
        replace: true,
      });
    },
    [navigate, search],
  );

  const stages = useQuery({
    queryKey: ['pipelineStages'],
    queryFn: () => request<{ stages: StageRow[] }>('/pipeline-stages'),
    // The pipeline is configuration: it changes when someone edits settings, not while
    // people work. Refetching it per navigation would add a request to every screen.
    staleTime: 10 * 60_000,
  });

  const options = useQuery({
    queryKey: ['jobFilterOptions'],
    queryFn: () => request<FilterOptions>('/jobs/filter-options'),
    staleTime: 5 * 60_000,
  });

  const view = useListView({
    routeId: '/jobs',
    defaultSort: 'scheduled',
    timeBased: true,
    defaultDateField: 'created',
    extra: useMemo(
      () => ({ stage, assignee, service, unassignedOnly }),
      [stage, assignee, service, unassignedOnly],
    ),
  });

  const [exportNote, setExportNote] = useState<{ text: string; failed: boolean } | null>(null);
  const [exporting, setExporting] = useState(false);

  // Two queries, one enabled at a time, both keyed the way `lib/realtime.ts` invalidates a
  // `job` change — so a colleague moving a stage refreshes whichever view is open.
  const board = useQuery({
    queryKey: ['board', 'jobs', view.exportParams.toString()],
    // The board is grouped server-side and capped per column, so it takes the filters without
    // the paging: a page number means nothing to a set of columns.
    queryFn: () => request<JobBoardResponse>(`/jobs/board?${view.exportParams}&limit=50`),
    enabled: view0 === 'board',
    placeholderData: (prev) => prev,
  });

  const list = useQuery({
    queryKey: ['jobs', 'list', view.params.toString()],
    queryFn: () => request<JobListResponse>(`/jobs?${view.params}`),
    enabled: view0 === 'list',
    // Holding the previous page on screen stops the table collapsing to an empty box every
    // time a filter moves.
    placeholderData: (prev) => prev,
  });

  const ductStages = stages.data ? toDuctStages(stages.data.stages, locale) : [];
  const data = list.data;

  const chips: FilterChip[] = [
    ...(stage
      ? [
          {
            id: 'stage',
            label: `${tr('job.stage')} : ${localized(stages.data?.stages.find((s) => s.id === stage)?.name ?? {}, locale)}`,
            onRemove: () => setSearch({ stage: undefined }),
          },
        ]
      : []),
    ...(assignee
      ? [
          {
            id: 'assignee',
            label: `${tr('job.assignees')} : ${options.data?.assignees.find((a) => a.id === assignee)?.name ?? assignee}`,
            onRemove: () => setSearch({ assignee: undefined }),
          },
        ]
      : []),
    ...(service
      ? [
          {
            id: 'service',
            label: `${tr('job.services')} : ${localized(options.data?.services.find((s) => s.id === service)?.name ?? {}, locale)}`,
            onRemove: () => setSearch({ service: undefined }),
          },
        ]
      : []),
    ...(unassignedOnly
      ? [{ id: 'unassigned', label: tr('job.filter.unassignedOnly'), onRemove: () => setSearch({ unassignedOnly: false }) }]
      : []),
  ];

  const runExport = async () => {
    setExporting(true);
    setExportNote(null);
    try {
      const outcome = await downloadExport('/jobs/export', view.exportParams, locale);
      setExportNote({
        text: outcome.truncated
          ? tr('list.exportTruncated', { limit: outcome.rows, total: outcome.matched })
          : tr('list.exportDone', { count: outcome.rows }),
        failed: outcome.truncated,
      });
    } catch {
      setExportNote({ text: tr('list.exportFailed'), failed: true });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={S.screen}>
      <header style={S.head}>
        <h1 style={S.title}>{tr('nav.jobs')}</h1>
        <div style={S.headActions}>
          <ViewSwitch view={view0} onChange={(next) => setSearch({ view: next })} t={tr} />
          {/* A `Link`, so raising a job can be opened in its own tab while the board stays put. */}
          <Link to="/jobs/new" className="btn btn--primary">
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <path d="M6 1.5v9M1.5 6h9" fill="none" stroke="currentColor" strokeWidth="1.6" />
            </svg>
            {tr('job.form.new')}
          </Link>
        </div>
      </header>

      <ListToolbar
        view={view}
        searchPlaceholder={tr('job.filter.searchPlaceholder')}
        chips={chips}
        onClearAll={() => setSearch({ stage: undefined, assignee: undefined, service: undefined, unassignedOnly: false })}
        dateFields={[
          { value: 'created', label: tr('list.field.created') },
          { value: 'scheduled', label: tr('list.field.scheduled') },
          { value: 'activity', label: tr('list.field.activity') },
        ]}
        total={view0 === 'list' ? data?.total : undefined}
        // Exporting is a list act. The board is a capped view of the same filters, so offering
        // it there would hand somebody a file that has nothing to do with what they can see.
        onExport={view0 === 'list' ? () => void runExport() : undefined}
        exporting={exporting}
        exportNote={exportNote?.text ?? null}
        exportFailed={exportNote?.failed}
      >
        {/* On the board the columns already are the stages, so this narrows to one column
            rather than filtering rows away — the same parameter, honest in both views. */}
        <Select
          label={view0 === 'board' ? tr('job.filter.stageColumn') : tr('job.stage')}
          value={stage ?? ''}
          onChange={(v) => setSearch({ stage: v || undefined })}
          placeholder={tr('job.filter.stageAll')}
          items={(stages.data?.stages ?? []).map((s) => ({ value: s.id, label: localized(s.name, locale) }))}
        />

        {/* Picking a person and asking for nobody are mutually exclusive, so each clears the other. */}
        <Select
          label={tr('job.assignees')}
          value={assignee ?? ''}
          onChange={(v) => setSearch({ assignee: v || undefined, unassignedOnly: v ? false : unassignedOnly })}
          placeholder={tr('job.filter.assigneeAll')}
          items={(options.data?.assignees ?? []).map((a) => ({ value: a.id, label: a.name }))}
        />

        <Select
          label={tr('job.services')}
          value={service ?? ''}
          onChange={(v) => setSearch({ service: v || undefined })}
          placeholder={tr('job.filter.serviceAll')}
          items={(options.data?.services ?? []).map((s) => ({ value: s.id, label: localized(s.name, locale) }))}
        />

        {/* 3149 of Hydrokleen's jobs have nobody on them: unassigned is the ordinary state of
            work waiting to be dispatched, so this is a plain filter carrying its count — not a
            warning, and never coloured like one. */}
        <button
          type="button"
          className="listbar__toggle"
          onClick={() => setSearch({ unassignedOnly: !unassignedOnly, assignee: undefined })}
          aria-pressed={unassignedOnly}
        >
          {tr('job.filter.unassignedOnly')}
          {options.data && <span className="tnum listbar__toggleCount">{options.data.unassignedCount}</span>}
        </button>
      </ListToolbar>

      {view0 === 'list' && (
        <ListWindowNotice
          view={view}
          outsideWindow={data?.outsideWindow ?? 0}
          empty={(data?.items.length ?? 0) === 0 && !list.isPending}
        />
      )}

      {view0 === 'board' && board.isError && <Failure error={board.error} onRetry={() => void board.refetch()} t={tr} />}
      {view0 === 'list' && list.isError && <Failure error={list.error} onRetry={() => void list.refetch()} t={tr} />}

      {view0 === 'board' ? (
        <Board
          columns={board.data?.columns ?? []}
          stages={stages.data?.stages ?? []}
          ductStages={ductStages}
          focusedStageId={stage}
          locale={locale}
          loading={board.isPending}
          onScanStage={(stageId) => setSearch({ view: 'list', stage: stageId })}
          t={tr}
        />
      ) : (
        <JobTable
          rows={data?.items ?? []}
          response={data}
          view={view}
          ductStages={ductStages}
          locale={locale}
          loading={list.isPending}
          busy={list.isFetching}
          t={tr}
        />
      )}
    </div>
  );
}

function ViewSwitch({ view, onChange, t }: { view: 'board' | 'list'; onChange: (v: 'board' | 'list') => void; t: T }) {
  return (
    <div style={S.switch} role="group" aria-label={t('job.view.label')}>
      {(['board', 'list'] as const).map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          aria-pressed={view === option}
          style={{ ...S.switchButton, ...(view === option ? S.switchButtonOn : null) }}
        >
          {option === 'board' ? <BoardGlyph /> : <ListGlyph />}
          {t(`job.view.${option}`)}
        </button>
      ))}
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  items,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  items: Array<{ value: string; label: string }>;
  placeholder?: string;
}) {
  // Shared toolbar classes rather than this file's inline styles: these selects now sit in the
  // same row as the ones on factures and clients, and three controls at three heights read as
  // three unrelated widgets.
  return (
    <label className="listbar__field">
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {items.map((item) => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * The dispatch board.
 *
 * A horizontal run of columns, one per stage flagged `onBoard`. The scroller itself is
 * focusable so a keyboard user can pan it, and every card is a link — tabbing walks the work
 * in reading order without any roving-tabindex machinery to get wrong.
 */
function Board({
  columns,
  stages,
  ductStages,
  focusedStageId,
  locale,
  loading,
  onScanStage,
  t,
}: {
  columns: JobBoardResponse['columns'];
  stages: StageRow[];
  ductStages: DuctStage[];
  focusedStageId?: string;
  locale: Locale;
  loading: boolean;
  onScanStage: (stageId: string) => void;
  t: T;
}) {
  const byStage = new Map(columns.map((c) => [c.stageId, c]));
  const visible = stages
    .filter((s) => s.onBoard && (!focusedStageId || s.id === focusedStageId))
    .sort((a, b) => a.position - b.position);

  if (loading && columns.length === 0) return <p style={S.quiet}>{t('common.loading')}</p>;

  return (
    <div style={S.board} tabIndex={0} role="region" aria-label={t('job.board.label')}>
      {visible.map((stage) => {
        const column = byStage.get(stage.id);
        const total = column?.total ?? 0;
        return (
          <section
            key={stage.id}
            style={S.column}
            aria-label={t('job.board.columnLabel', { stage: localized(stage.name, locale), count: total })}
          >
            <header style={{ ...S.columnHead, borderTopColor: STAGE_COLOR[stage.kind] }}>
              <span style={S.columnName}>{localized(stage.name, locale)}</span>
              <span className="tnum" style={S.columnCount}>
                {total}
              </span>
            </header>

            <ul style={S.cards} role="list">
              {(column?.items ?? []).map((job) => (
                <li key={job.id}>
                  <JobCard job={job} ductStages={ductStages} locale={locale} t={t} />
                </li>
              ))}
            </ul>

            {total === 0 && <p style={S.emptyColumn}>{t('job.board.empty')}</p>}

            {/* A column is capped. The honest move is to hand the remainder to the list view,
                which is built for volume, rather than scroll a thousand cards. */}
            {column && total > column.items.length && (
              <button type="button" style={S.columnMore} onClick={() => onScanStage(stage.id)}>
                {t('job.board.more', { count: total })}
              </button>
            )}
          </section>
        );
      })}
    </div>
  );
}

function JobCard({ job, ductStages, locale, t }: { job: JobRow; ductStages: DuctStage[]; locale: Locale; t: T }) {
  const balance = toCents(job.rollup.balance);
  const slot = formatWindow(job.scheduledStart, job.scheduledEnd, locale);

  return (
    <Link to="/jobs/$jobId" params={{ jobId: job.id }} style={S.card}>
      {/*
        * No pipeline bar here, deliberately.
        *
        * On a board the column *is* the stage — every card in it is at the same point, so a
        * per-card bar restates the heading forty times and competes with the one thing the
        * card should carry: whose job it is and when. The bar earns its place in the list
        * view, where there are no columns to say it.
        */}
      <span style={S.cardTop}>
        <span className="tnum" style={S.cardNumber}>
          {job.number}
        </span>
      </span>

      <span style={S.cardCustomer}>{job.customer.name}</span>

      {job.address && (
        <span style={S.cardAddress}>
          {job.address.line1}, {job.address.city}
        </span>
      )}

      <span style={S.cardFoot}>
        <span className="tnum" style={slot ? S.when : S.whenAbsent}>
          {slot ?? t('job.unscheduled')}
        </span>
        <Assignees people={job.assignees} t={t} />
      </span>

      {balance > 0 && (
        <span style={S.cardBalance}>
          <span className="label">{t('job.balanceDue')}</span>
          <span className="money">{formatMoney(balance, locale)}</span>
        </span>
      )}
    </Link>
  );
}

/**
 * The dense table.
 *
 * The duct run in every row is the point of this view: a coordinator scanning forty jobs
 * reads position-in-pipeline as a shape, instead of parsing forty French stage names.
 */
function JobTable({
  rows,
  response,
  view,
  ductStages,
  locale,
  loading,
  busy,
  t,
}: {
  rows: JobRow[];
  response: JobListResponse | undefined;
  view: ListView;
  ductStages: DuctStage[];
  locale: Locale;
  loading: boolean;
  busy: boolean;
  t: T;
}) {
  if (loading && rows.length === 0) return <p style={S.quiet}>{t('common.loading')}</p>;
  if (rows.length === 0) return <p style={S.quiet}>{t('common.noResults')}</p>;

  // From the response, never from the URL: on a first load the URL names no sort at all and only
  // the API knows what its default is, so a heading reading the URL would draw no arrow anywhere.
  const sort = response?.sort;
  const dir = response?.dir;

  return (
    <div style={S.tableWrap}>
      <table style={S.table} aria-label={t('job.list.label')}>
        {/*
          * Headings that sort, and headings that do not.
          *
          * The duct run, the address and the crew are deliberately not sortable: ordering by a
          * pipeline shape means nothing, and sorting 4 000 jobs alphabetically by street answers
          * no question a dispatcher has. Offering a control that does something useless is worse
          * than not offering it.
          */}
        <thead>
          <tr>
            <SortHeader field="number" label={t('job.number')} sort={sort} dir={dir} onSort={view.setSort} />
            <th scope="col" style={{ ...S.th, width: 156 }}>
              {t('job.stage')}
            </th>
            <SortHeader field="customer" label={t('customer.one')} sort={sort} dir={dir} onSort={view.setSort} />
            <th scope="col" style={S.th}>
              {t('customer.address')}
            </th>
            <SortHeader field="scheduled" label={t('job.scheduled')} sort={sort} dir={dir} onSort={view.setSort} />
            <th scope="col" style={S.th}>
              {t('job.assignees')}
            </th>
            <SortHeader field="balance" label={t('invoice.balance')} sort={sort} dir={dir} onSort={view.setSort} numeric />
          </tr>
        </thead>
        <tbody>
          {rows.map((job) => {
            const balance = toCents(job.rollup.balance);
            const slot = formatWindow(job.scheduledStart, job.scheduledEnd, locale);
            return (
              <tr key={job.id} style={S.tr}>
                <td style={S.td}>
                  <Link to="/jobs/$jobId" params={{ jobId: job.id }} className="tnum" style={S.rowLink}>
                    {job.number}
                  </Link>
                </td>
                <td style={S.td}>
                  <DuctRun
                    stages={ductStages}
                    currentStageId={job.stage.id}
                    variant="row"
                    label={t('job.list.stageOf', { number: job.number })}
                  />
                </td>
                <td style={S.td}>
                  <Link to="/customers/$customerId" params={{ customerId: job.customer.id }} style={S.link}>
                    {job.customer.name}
                  </Link>
                  {job.title && <span style={S.subtle}>{job.title}</span>}
                </td>
                <td style={S.td}>
                  {job.address ? (
                    <>
                      {job.address.line1}
                      <span style={S.subtle}>{job.address.city}</span>
                    </>
                  ) : (
                    <span style={S.absent}>—</span>
                  )}
                </td>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                  <span className="tnum" style={slot ? undefined : S.absent}>
                    {slot ?? t('job.unscheduled')}
                  </span>
                </td>
                <td style={S.td}>
                  <Assignees people={job.assignees} t={t} />
                </td>
                <td style={{ ...S.td, textAlign: 'right' }}>
                  {balance > 0 ? (
                    <span className="money">{formatMoney(balance, locale)}</span>
                  ) : (
                    <span style={S.absent}>—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/*
        * Numbered pages, replacing a forward-only "next".
        *
        * The total is a real count — the platform this replaces echoed the page size back as the
        * total and silently truncated our own first export — and with a real count the pager can
        * say how many pages there are instead of only whether one more exists.
        */}
      {response && (
        <Pager
          page={response.page ?? view.page}
          pageCount={response.pageCount ?? 1}
          pageSize={response.pageSize ?? view.pageSize}
          total={response.total}
          count={rows.length}
          label={t('job.many').toLowerCase()}
          busy={busy}
          onPage={view.setPage}
          onPageSize={view.setPageSize}
        />
      )}
    </div>
  );
}

/**
 * Who is on the job.
 *
 * "Nobody yet" is the resting state of thousands of these rows — work sitting in the
 * dispatch backlog — so it reads as ordinary metadata. Colouring it as a problem would paint
 * most of the board red and teach everyone to ignore the colour.
 */
function Assignees({ people, t }: { people: JobRow['assignees'] | undefined; t: T }) {
  // Defended rather than assumed: an endpoint that stops sending this should cost one column,
  // not the whole screen. It cost the whole screen once.
  const list = people ?? [];
  if (list.length === 0) return <span style={S.absent}>{t('job.unassigned')}</span>;

  return (
    <span style={S.avatars}>
      {list.slice(0, 3).map((person) => (
        <span
          key={person.id}
          style={{ ...S.avatar, ...(person.isLead ? S.avatarLead : null) }}
          title={person.name}
          aria-label={person.name}
        >
          {person.initials}
        </span>
      ))}
      {list.length > 3 && (
        <span className="tnum" style={S.avatarRest}>
          +{list.length - 3}
        </span>
      )}
    </span>
  );
}

function Failure({ error, onRetry, t }: { error: unknown; onRetry: () => void; t: T }) {
  // A 403 is an answer, not a failure: retrying it produces the same 403 four times over.
  const forbidden = error instanceof ApiError && error.status === 403;
  return (
    <div style={S.failure} role="alert">
      <span>{forbidden ? t('errors.forbidden') : t('errors.generic')}</span>
      {!forbidden && (
        <button type="button" style={S.more} onClick={onRetry}>
          {t('common.retry')}
        </button>
      )}
    </div>
  );
}

/**
 * Scheduled window, e.g. `jeu. 12 sept. 08:00–10:00`.
 *
 * Formatters are cached per locale: a hundred-row table would otherwise construct three
 * hundred `Intl.DateTimeFormat` instances, which is the most expensive thing a dense grid
 * can do to itself.
 */
const formatters = new Map<string, Intl.DateTimeFormat>();
function formatter(locale: Locale, key: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const id = `${locale}:${key}`;
  let existing = formatters.get(id);
  if (!existing) {
    existing = new Intl.DateTimeFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', options);
    formatters.set(id, existing);
  }
  return existing;
}

function formatWindow(startIso: string | null, endIso: string | null, locale: Locale): string | null {
  if (!startIso) return null;
  const start = new Date(startIso);
  const day = formatter(locale, 'day', { weekday: 'short', day: 'numeric', month: 'short' }).format(start);
  const clock = formatter(locale, 'clock', { hour: '2-digit', minute: '2-digit' });
  if (!endIso) return `${day} ${clock.format(start)}`;
  return `${day} ${clock.format(start)}–${clock.format(new Date(endIso))}`;
}

function BoardGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M2.5 3.5h3v9h-3zM6.5 3.5h3v6h-3zM10.5 3.5h3v10h-3z" />
    </svg>
  );
}

function ListGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M2.5 4.5h11M2.5 8h11M2.5 11.5h11" />
    </svg>
  );
}

/**
 * Styles are colocated because these two screens are the first to need them, and every
 * value resolves to a token from `tokens.css` — no hex, no colour chosen by hand.
 */
const S = {
  screen: { display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', padding: 'var(--space-6)', minWidth: 0 },
  head: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--space-4)' },
  title: { fontSize: 'var(--text-xl)' },
  headActions: { display: 'flex', alignItems: 'center', gap: 'var(--space-3)' },

  switch: {
    display: 'inline-flex',
    border: '1px solid var(--border-strong)',
    borderRadius: 'var(--radius-md)',
    overflow: 'hidden',
  },
  switchButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-2) var(--space-3)',
    background: 'var(--surface-raised)',
    color: 'var(--text-secondary)',
    border: 'none',
    font: '600 var(--text-sm)/1 var(--font-body)',
    cursor: 'pointer',
  },
  switchButtonOn: { background: 'var(--accent-wash)', color: 'var(--accent)' },

  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: 'var(--space-3)',
    padding: 'var(--space-3)',
    background: 'var(--surface-raised)',
    border: '1px solid var(--border-hairline)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-sm)',
  },
  field: { display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' },
  select: {
    padding: 'var(--space-2)',
    font: '500 var(--text-sm)/1.2 var(--font-body)',
    color: 'var(--text-primary)',
    background: 'var(--surface-raised)',
    border: '1px solid var(--border-strong)',
    borderRadius: 'var(--radius-sm)',
  },
  searchInput: {
    flex: '1 1 220px',
    minWidth: 180,
    padding: 'var(--space-2) var(--space-3)',
    font: '400 var(--text-base)/1.2 var(--font-body)',
    color: 'var(--text-primary)',
    background: 'var(--surface-sunken)',
    border: '1px solid var(--border-strong)',
    borderRadius: 'var(--radius-sm)',
  },
  toggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-2) var(--space-3)',
    background: 'var(--surface-sunken)',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border-strong)',
    borderRadius: 'var(--radius-sm)',
    font: '600 var(--text-sm)/1 var(--font-body)',
    cursor: 'pointer',
  },
  toggleOn: { background: 'var(--accent-wash)', borderColor: 'var(--accent)', color: 'var(--accent)' },
  toggleCount: { fontSize: 'var(--text-xs)', color: 'var(--text-muted)' },
  clear: {
    padding: 'var(--space-2)',
    background: 'none',
    border: 'none',
    color: 'var(--accent)',
    font: '600 var(--text-sm)/1 var(--font-body)',
    cursor: 'pointer',
  },

  board: {
    display: 'flex',
    gap: 'var(--space-3)',
    overflowX: 'auto',
    paddingBottom: 'var(--space-3)',
    alignItems: 'flex-start',
  },
  column: {
    flex: '0 0 288px',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-2)',
    background: 'var(--surface-sunken)',
    border: '1px solid var(--border-hairline)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--space-2)',
  },
  columnHead: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 'var(--space-2)',
    // The stage's colour as a folded edge on top of the column, not a filled banner.
    borderTop: '2px solid',
    padding: 'var(--space-2) var(--space-1) var(--space-1)',
  },
  columnName: { font: '700 var(--text-sm)/1.2 var(--font-display)', letterSpacing: 'var(--tracking-display)' },
  columnCount: { fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' },
  cards: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' },
  emptyColumn: { margin: 0, padding: 'var(--space-4) var(--space-2)', color: 'var(--text-muted)', fontSize: 'var(--text-xs)' },
  columnMore: {
    padding: 'var(--space-2)',
    background: 'none',
    border: 'none',
    textAlign: 'left',
    color: 'var(--accent)',
    font: '600 var(--text-xs)/1.2 var(--font-body)',
    cursor: 'pointer',
  },

  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-1)',
    padding: 'var(--space-3)',
    background: 'var(--surface-raised)',
    border: '1px solid var(--border-hairline)',
    borderRadius: 'var(--radius-sm)',
    boxShadow: 'var(--shadow-sm)',
    color: 'var(--text-primary)',
    textDecoration: 'none',
  },
  cardTop: { display: 'grid', gridTemplateColumns: 'auto 1fr', alignItems: 'center', gap: 'var(--space-3)' },
  cardNumber: { fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' },
  cardCustomer: { font: '600 var(--text-base)/1.3 var(--font-body)' },
  cardAddress: { fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' },
  cardFoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--space-2)',
    marginTop: 'var(--space-1)',
  },
  cardBalance: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 'var(--space-2)',
    fontSize: 'var(--text-sm)',
  },
  when: { fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' },
  whenAbsent: { fontSize: 'var(--text-xs)', color: 'var(--text-muted)' },

  tableWrap: {
    background: 'var(--surface-raised)',
    border: '1px solid var(--border-hairline)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-sm)',
    overflowX: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' },
  th: {
    position: 'sticky',
    top: 0,
    background: 'var(--surface-raised)',
    textAlign: 'left',
    padding: 'var(--space-2) var(--space-3)',
    borderBottom: '1px solid var(--border-strong)',
    font: '600 var(--text-2xs)/1 var(--font-body)',
    letterSpacing: 'var(--tracking-label)',
    textTransform: 'uppercase',
    color: 'var(--text-secondary)',
    whiteSpace: 'nowrap',
  },
  tr: { borderBottom: '1px solid var(--border-hairline)' },
  td: { padding: 'var(--space-2) var(--space-3)', verticalAlign: 'middle' },
  rowLink: { color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' },
  link: { color: 'var(--text-primary)', fontWeight: 600, textDecoration: 'none' },
  subtle: { display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' },
  absent: { color: 'var(--text-muted)', fontSize: 'var(--text-xs)' },
  tableFoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--space-3)',
    padding: 'var(--space-2) var(--space-3)',
    borderTop: '1px solid var(--border-hairline)',
    fontSize: 'var(--text-xs)',
    color: 'var(--text-secondary)',
  },
  more: {
    padding: 'var(--space-2) var(--space-3)',
    background: 'var(--surface-sunken)',
    border: '1px solid var(--border-strong)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text-primary)',
    font: '600 var(--text-sm)/1 var(--font-body)',
    cursor: 'pointer',
  },

  avatars: { display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' },
  avatar: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: 'var(--radius-full)',
    background: 'var(--surface-sunken)',
    border: '1px solid var(--border-strong)',
    font: '600 var(--text-2xs)/1 var(--font-body)',
    color: 'var(--text-secondary)',
  },
  avatarLead: { background: 'var(--accent-wash)', borderColor: 'var(--accent)', color: 'var(--accent)' },
  avatarRest: { fontSize: 'var(--text-2xs)', color: 'var(--text-muted)' },

  quiet: { color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' },
  failure: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-3)',
    padding: 'var(--space-3)',
    background: 'var(--state-negative-wash)',
    border: '1px solid var(--state-negative)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--text-sm)',
  },
} satisfies Record<string, CSSProperties>;
